import React, { useState } from 'react';
import { Quote, ArrowRight, Star, MessageSquare, ExternalLink, MapPin } from 'lucide-react';
import { googleReviews, verifiedStats } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const TestimonialsPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge badge-teal mb-4">Patient Experiences</span>
            <h1 className="heading-hero mb-6">What Our Patients Say</h1>
            <p className="body-large">
              Read verified reviews from patients and families who have experienced 
              compassionate, expert care with Dysphagia Therapy Plus.
            </p>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            {/* Google Reviews Card */}
            <div className="bg-gradient-to-br from-teal-50 to-white border border-teal-200 rounded-2xl p-8 md:p-10 text-center">
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={28} className="text-amber-400 fill-current" />
                ))}
              </div>
              
              <h2 className="heading-2 mb-3 text-gray-900">{googleReviews.heading}</h2>
              <p className="body-medium text-gray-600 mb-6 max-w-xl mx-auto">
                {googleReviews.subheading}
              </p>
              
              {/* Verified Business Info */}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-8">
                <MapPin size={16} className="text-teal-600" />
                <span>{googleReviews.businessAddress}</span>
              </div>
              
              {/* Google Review CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href={googleReviews.reviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="testimonials-read-reviews-cta"
                >
                  <Star size={18} className="fill-current" />
                  {googleReviews.ctaRead}
                  <ExternalLink size={14} />
                </a>
                <a 
                  href={googleReviews.reviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="testimonials-leave-review-cta"
                >
                  <MessageSquare size={18} />
                  {googleReviews.ctaLeave}
                </a>
              </div>
            </div>
            
            {/* Social Proof Note */}
            <p className="text-center text-sm text-gray-500 mt-6">
              All reviews are from verified Google users. We never solicit or incentivize reviews.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="py-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: String(verifiedStats.yearsExperience), label: 'Years Experience' },
                { value: String(verifiedStats.patientsServed), label: 'Patients Served' },
                { value: verifiedStats.certification, label: verifiedStats.certificationLabel },
                { value: '✓', label: 'Insurance Accepted' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-teal-600 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Start Your Success Story</h2>
            <p className="body-large mb-8">
              Join the hundreds of patients who have improved their swallowing 
              and quality of life. Your journey begins with one step.
            </p>
            <button
              onClick={() => setIsBookingOpen(true)}
              className="btn-primary"
            >
              Request an Evaluation
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} sourcePage="testimonials" />
    </>
  );
};

export default TestimonialsPage;
